// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file Onboarding/index.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Gestao de telas de onboarding da app mobile
// @date 2026-01-18
// ------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid2 as Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  DragIndicator as DragIcon,
  Smartphone as MobileIcon,
  Computer as WebIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  CloudUpload as UploadIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { onboardingService } from '../../../services';
import type { OnboardingScreen, OnboardingInput } from '../../../services';

const Onboarding: React.FC = () => {
  // Data state
  const [screens, setScreens] = useState<OnboardingScreen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableExists, setTableExists] = useState<boolean | null>(null);

  // Dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<OnboardingScreen | null>(null);
  const [formData, setFormData] = useState<OnboardingInput>({
    titulo: '',
    descricao: '',
    imagemUrl: '',
    corFundo: '#FFFFFF',
    corTitulo: '#333333',
    corDescricao: '#666666',
    botaoTexto: 'Continuar',
    botaoCor: '#6233A0',
    plataforma: 'MOBILE',
    estado: 'A',
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  // Check table status and load data
  const checkAndLoad = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const statusResponse = await onboardingService.checkStatus();
      setTableExists(statusResponse.tableExists);

      if (statusResponse.tableExists) {
        const listResponse = await onboardingService.list();
        if (listResponse.success) {
          setScreens(listResponse.data);
        }
      }
    } catch (err: any) {
      console.error('Error loading onboarding:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAndLoad();
  }, [checkAndLoad]);

  // Run migration
  const handleMigrate = async () => {
    setLoading(true);
    try {
      const result = await onboardingService.migrate();
      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: 'success',
        });
        await checkAndLoad();
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Erro ao criar tabela',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Open edit dialog
  const handleEdit = (screen?: OnboardingScreen) => {
    if (screen) {
      setSelectedScreen(screen);
      setFormData({
        titulo: screen.titulo,
        descricao: screen.descricao || '',
        imagemUrl: screen.imagemUrl || '',
        corFundo: screen.corFundo,
        corTitulo: screen.corTitulo,
        corDescricao: screen.corDescricao,
        botaoTexto: screen.botaoTexto,
        botaoCor: screen.botaoCor,
        plataforma: screen.plataforma,
        estado: screen.estado,
      });
      if (screen.temImagem) {
        setImagePreview(onboardingService.getImageUrl(screen.id));
      } else if (screen.imagemUrl) {
        setImagePreview(screen.imagemUrl);
      } else {
        setImagePreview(null);
      }
    } else {
      setSelectedScreen(null);
      setFormData({
        titulo: '',
        descricao: '',
        imagemUrl: '',
        corFundo: '#FFFFFF',
        corTitulo: '#333333',
        corDescricao: '#666666',
        botaoTexto: 'Continuar',
        botaoCor: '#6233A0',
        plataforma: 'MOBILE',
        estado: 'A',
      });
      setImagePreview(null);
    }
    setEditDialog(true);
  };

  // Handle image upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setImagePreview(base64);
        setFormData({
          ...formData,
          imagemBase64: base64,
          mimeType: file.type,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Save screen
  const handleSave = async () => {
    if (!formData.titulo) {
      setSnackbar({
        open: true,
        message: 'Titulo e obrigatorio',
        severity: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      if (selectedScreen) {
        await onboardingService.update(selectedScreen.id, formData);
        setSnackbar({
          open: true,
          message: 'Tela atualizada com sucesso',
          severity: 'success',
        });
      } else {
        await onboardingService.create(formData);
        setSnackbar({
          open: true,
          message: 'Tela criada com sucesso',
          severity: 'success',
        });
      }
      setEditDialog(false);
      await checkAndLoad();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Erro ao guardar',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Delete screen
  const handleDelete = async () => {
    if (!selectedScreen) return;

    setSaving(true);
    try {
      await onboardingService.delete(selectedScreen.id);
      setSnackbar({
        open: true,
        message: 'Tela eliminada com sucesso',
        severity: 'success',
      });
      setDeleteDialog(false);
      setSelectedScreen(null);
      await checkAndLoad();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Erro ao eliminar',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Move screen up/down
  const handleMove = async (screen: OnboardingScreen, direction: 'up' | 'down') => {
    const currentIndex = screens.findIndex((s) => s.id === screen.id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === screens.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newScreens = [...screens];
    const temp = newScreens[currentIndex];
    newScreens[currentIndex] = newScreens[newIndex];
    newScreens[newIndex] = temp;

    // Update orders
    const items = newScreens.map((s, idx) => ({ id: s.id, ordem: idx + 1 }));

    try {
      await onboardingService.reorder(items);
      setScreens(newScreens.map((s, idx) => ({ ...s, ordem: idx + 1 })));
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao reordenar',
        severity: 'error',
      });
    }
  };

  // Render migration needed
  if (!loading && tableExists === false) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Onboarding
        </Typography>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleMigrate}>
              Criar Tabela
            </Button>
          }
        >
          A tabela de onboarding ainda nao existe. Clique para criar.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Onboarding
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie as telas de introducao da aplicacao mobile
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={checkAndLoad} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Pre-visualizar">
            <IconButton onClick={() => setPreviewDialog(true)} disabled={screens.length === 0}>
              <PreviewIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleEdit()}
          >
            Nova Tela
          </Button>
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Screens List */}
      {!loading && screens.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <MobileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Nenhuma tela de onboarding
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Adicione telas para mostrar aos utilizadores quando abrem a app pela primeira vez.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleEdit()}>
            Adicionar Primeira Tela
          </Button>
        </Paper>
      )}

      {!loading && screens.length > 0 && (
        <Grid container spacing={2}>
          {screens.map((screen, index) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={screen.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: screen.estado === 'I' ? '2px dashed #ccc' : undefined,
                  opacity: screen.estado === 'I' ? 0.7 : 1,
                }}
              >
                {/* Image Preview */}
                <Box
                  sx={{
                    height: 180,
                    backgroundColor: screen.corFundo,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  {screen.temImagem || screen.imagemUrl ? (
                    <Box
                      component="img"
                      src={
                        screen.temImagem
                          ? onboardingService.getImageUrl(screen.id)
                          : screen.imagemUrl
                      }
                      alt={screen.titulo}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                      onError={(e: any) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <ImageIcon sx={{ fontSize: 64, color: '#ccc' }} />
                  )}
                  <Chip
                    label={`#${screen.ordem}`}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                    }}
                  />
                  {screen.estado === 'I' && (
                    <Chip
                      label="Inativo"
                      size="small"
                      color="warning"
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    />
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: screen.corTitulo }}
                  >
                    {screen.titulo}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: screen.corDescricao, mb: 2 }}
                  >
                    {screen.descricao || 'Sem descricao'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={screen.plataforma === 'MOBILE' ? <MobileIcon /> : <WebIcon />}
                      label={screen.plataforma}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={screen.botaoTexto}
                      size="small"
                      sx={{
                        backgroundColor: screen.botaoCor,
                        color: 'white',
                      }}
                    />
                  </Box>
                </CardContent>

                <Divider />

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
                  <Box>
                    <Tooltip title="Mover para cima">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMove(screen, 'up')}
                          disabled={index === 0}
                        >
                          <UpIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Mover para baixo">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMove(screen, 'down')}
                          disabled={index === screens.length - 1}
                        >
                          <DownIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Box>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEdit(screen)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedScreen(screen);
                          setDeleteDialog(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedScreen ? 'Editar Tela' : 'Nova Tela de Onboarding'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Titulo"
                fullWidth
                required
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Texto do Botao"
                fullWidth
                value={formData.botaoTexto}
                onChange={(e) => setFormData({ ...formData, botaoTexto: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Descricao"
                fullWidth
                multiline
                rows={3}
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="URL da Imagem (opcional)"
                fullWidth
                value={formData.imagemUrl}
                onChange={(e) => setFormData({ ...formData, imagemUrl: e.target.value })}
                helperText="Ex: assets/images/onboarding1.png"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                >
                  Upload Imagem
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
                {imagePreview && (
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{ height: 56, maxWidth: 100, objectFit: 'contain' }}
                  />
                )}
              </Box>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                label="Cor de Fundo"
                type="color"
                fullWidth
                value={formData.corFundo}
                onChange={(e) => setFormData({ ...formData, corFundo: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: formData.corFundo,
                        borderRadius: 1,
                        mr: 1,
                      }}
                    />
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                label="Cor do Titulo"
                type="color"
                fullWidth
                value={formData.corTitulo}
                onChange={(e) => setFormData({ ...formData, corTitulo: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                label="Cor da Descricao"
                type="color"
                fullWidth
                value={formData.corDescricao}
                onChange={(e) => setFormData({ ...formData, corDescricao: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                label="Cor do Botao"
                type="color"
                fullWidth
                value={formData.botaoCor}
                onChange={(e) => setFormData({ ...formData, botaoCor: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.estado === 'A'}
                    onChange={(e) =>
                      setFormData({ ...formData, estado: e.target.checked ? 'A' : 'I' })
                    }
                  />
                }
                label="Tela ativa"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : undefined}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirmar Eliminacao</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja eliminar a tela "{selectedScreen?.titulo}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Pre-visualizacao Mobile</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              width: '100%',
              maxWidth: 375,
              mx: 'auto',
              border: '8px solid #333',
              borderRadius: 4,
              overflow: 'hidden',
              backgroundColor: screens[0]?.corFundo || '#fff',
            }}
          >
            {screens
              .filter((s) => s.estado === 'A')
              .slice(0, 1)
              .map((screen) => (
                <Box key={screen.id} sx={{ p: 3, textAlign: 'center' }}>
                  {(screen.temImagem || screen.imagemUrl) && (
                    <Box
                      component="img"
                      src={
                        screen.temImagem
                          ? onboardingService.getImageUrl(screen.id)
                          : screen.imagemUrl
                      }
                      alt={screen.titulo}
                      sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'contain',
                        mb: 2,
                      }}
                    />
                  )}
                  <Typography
                    variant="h5"
                    sx={{ color: screen.corTitulo, fontWeight: 'bold', mb: 1 }}
                  >
                    {screen.titulo}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: screen.corDescricao, mb: 3 }}
                  >
                    {screen.descricao}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 2 }}>
                    {screens.filter((s) => s.estado === 'A').map((_, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          width: idx === 0 ? 12 : 8,
                          height: idx === 0 ? 12 : 8,
                          borderRadius: '50%',
                          backgroundColor: idx === 0 ? screen.botaoCor : '#ccc',
                        }}
                      />
                    ))}
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: screen.botaoCor,
                      '&:hover': { backgroundColor: screen.botaoCor },
                    }}
                  >
                    {screen.botaoTexto}
                  </Button>
                </Box>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Onboarding;
